// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {useRef, useState, useEffect, useCallback, useMemo,
    useLayoutEffect, UIEvent} from 'react';
import cn from 'classnames';
import {Basic_input} from '../input/basic_input';
import Icon_search from '../../img/icons/16/search.svg';

import styles from './select.module.less';

export interface i_basic_select_option {
    value: string | number;
    label: string;
    disabled?: boolean;
}

export interface i_render_body_props {
    selected: boolean;
    opened: boolean;
    value: string | number | null;
    focused: boolean;
    disabled: boolean;
}

export interface i_render_option_props {
    option: i_basic_select_option;
    selected: boolean;
    highlighted: boolean;
    focused: boolean;
}

export interface i_basic_select_props {
    value?: string | number | null;
    default_value?: string | number | null;
    options: Array<i_basic_select_option>;

    on_change?: (value: string | number | null)=>void;
    on_open?: ()=>void;
    on_close?: ()=>void;
    on_focus?: (e: React.FocusEvent)=>void;
    on_blur?: (e: React.FocusEvent)=>void;
    on_option_enter?: (value: string|number, el: HTMLElement)=>void;
    on_option_leave?: ()=>void;

    render_body: (props: i_render_body_props)=>React.ReactNode;
    render_option: (props: i_render_option_props)=>React.ReactNode;
    render_label?: ()=>React.ReactNode;
    render_empty?: ()=>React.ReactNode;
    render_loading?: ()=>React.ReactNode;

    cls?: string;
    disabled?: boolean;
    tab_index?: number;
    ignore_highlight_first_element?: boolean;
    ignore_click?: boolean;
    with_full_search?: boolean;
    full_search_placeholder?: string;
    search_mode?: 'starts_with' | 'includes';
    search_autofocus?: boolean;
    disable_unselect?: boolean;
    position?: 'right' | 'left';
    virtualize?: boolean;
    item_height?: number;
    overscan?: number;
    loading?: boolean;
    load_options?: ()=>Promise<void>;
}

const use_select = (props: i_basic_select_props)=>{
    const {
        value: controlled_value,
        default_value = null,
        options = [],
        on_change,
        on_open,
        on_close,
        disabled = false,
        ignore_highlight_first_element,
        with_full_search,
        search_mode = 'includes',
        disable_unselect,
    } = props;

    const [is_open, set_is_open] = useState(false);
    const [internal_value, set_internal_value] =
        useState<string | number | null>(default_value);
    const [focused, set_focused] = useState(false);
    const [highlighted_idx, set_highlighted_idx] = useState(-1);
    const [search_text, set_search_text] = useState('');
    const highlight_source = useRef<'keyboard' | 'initial' | null>(null);
    const is_first_open = useRef(true);
    const value = controlled_value !== undefined ? controlled_value
        : internal_value;
    const has_selected_value = value !== null;

    const toggle_dropdown = useCallback((state?: boolean)=>{
        if (disabled)
            return;
        set_is_open(current=>{
            const new_state = state ?? !current;
            if (new_state)
            {
                if (on_open)
                    on_open();
            }
            else if (on_close)
                on_close();
            return new_state;
        });
    }, [disabled, on_open, on_close]);

    const handle_option_select = useCallback((option: i_basic_select_option)=>{
        if (option.disabled || props.ignore_click)
            return;
        const new_value = disable_unselect && value == option.value ?
            option.value : value == option.value ? null : option.value;
        if (controlled_value === undefined)
            set_internal_value(new_value);
        if (on_change)
            on_change(new_value);
        set_is_open(false);
        if (on_close)
            on_close();
    }, [value, controlled_value, on_change, on_close, disable_unselect]);

    const search_query = useRef('');
    const search_timeout = useRef<number>();

    useEffect(()=>{
        return ()=>{
            if (search_timeout.current)
                window.clearTimeout(search_timeout.current);
        };
    }, []);

    const filtered_options = useMemo(()=>{
        let filtered = options.filter(opt=>!opt.disabled);
        if (with_full_search && search_text)
        {
            const search_lower = search_text.toLowerCase();
            filtered = filtered.filter(opt=>{
                const label_lower = opt.label?.toLowerCase();
                if (!label_lower)
                    return false;
                const str_method = search_mode === 'starts_with' ?
                    'startsWith' : 'includes';
                return label_lower[str_method](search_lower);
            });
        }
        return filtered;
    }, [options, with_full_search, search_text, search_mode]);

    useEffect(()=>{
        if (with_full_search)
        {
            set_highlighted_idx(filtered_options.length > 0 ? 0 : -1);
            if (filtered_options.length > 0)
                highlight_source.current = 'keyboard';
        }
    }, [search_text, filtered_options.length, with_full_search]);

    const handle_key_down = useCallback((e: React.KeyboardEvent)=>{
        if (disabled)
            return;
        switch (e.key)
        {
            case 'ArrowDown':
                e.preventDefault();
                highlight_source.current = 'keyboard';
                if (!is_open)
                    set_is_open(true);
                else
                {
                    set_highlighted_idx(prev=>
                        prev < filtered_options.length - 1 ? prev + 1 :
                        ignore_highlight_first_element ? -1 : 0);
                }
                break;
            case 'ArrowUp':
                e.preventDefault();
                highlight_source.current = 'keyboard';
                if (!is_open)
                    set_is_open(true);
                else
                {
                    set_highlighted_idx(prev=>
                        prev > 0 ? prev - 1 : filtered_options.length - 1);
                }
                break;
            case 'Enter':
                e.preventDefault();
                if (is_open && highlighted_idx >= 0)
                    handle_option_select(filtered_options[highlighted_idx]);
                else
                    toggle_dropdown();
                break;
            case 'Escape':
                e.preventDefault();
                if (is_open)
                {
                    set_is_open(false);
                    if (on_close)
                        on_close();
                }
                break;
            default:
                if (e.key.length == 1 && !e.ctrlKey && !e.altKey && !e.metaKey
                  && !with_full_search)
                {
                    const new_query = search_query.current
                        + e.key.toLowerCase();
                    if (new_query.length == 1)
                    {
                        highlight_source.current = 'keyboard';
                        const next_idx = filtered_options.findIndex(
                            (opt, idx)=>idx > highlighted_idx &&
                                opt.label?.toLowerCase()
                                    .startsWith(new_query));
                        if (next_idx >= 0)
                            set_highlighted_idx(next_idx);
                        else
                        {
                            const first_idx = filtered_options.findIndex(opt=>
                                opt.label?.toLowerCase()
                                    .startsWith(new_query));
                            if (first_idx >= 0)
                                set_highlighted_idx(first_idx);
                        }
                    }
                    else
                    {
                        highlight_source.current = 'keyboard';
                        const first_idx = filtered_options.findIndex(opt=>
                            opt.label?.toLowerCase().startsWith(new_query));
                        if (first_idx >= 0)
                            set_highlighted_idx(first_idx);
                    }
                    search_query.current = new_query;
                    if (search_timeout.current)
                        window.clearTimeout(search_timeout.current);
                    search_timeout.current = window.setTimeout(()=>{
                        search_query.current = '';
                    }, 500);
                }
                break;
        }
    }, [disabled, is_open, highlighted_idx, filtered_options,
        handle_option_select, on_close, toggle_dropdown, with_full_search]);

    useEffect(()=>{
        if (is_open)
        {
            if (is_first_open.current)
            {
                highlight_source.current = 'initial';
                is_first_open.current = false;
            }
            if (value !== null)
            {
                const selected_idx = filtered_options
                    .findIndex(opt=>opt.value == value);
                if (selected_idx >= 0)
                    set_highlighted_idx(selected_idx);
                else if (filtered_options.length)
                {
                    set_highlighted_idx(
                        ignore_highlight_first_element ? -1 : 0);
                }
            }
            else if (filtered_options.length)
            {
                set_highlighted_idx(
                    ignore_highlight_first_element ? -1 : 0);
            }
        }
        else
        {
            set_highlighted_idx(-1);
            is_first_open.current = true;
        }
    }, [is_open, filtered_options, value]);

    return {
        is_open,
        focused,
        highlighted_idx,
        value,
        has_selected_value,
        toggle_dropdown,
        handle_option_select,
        handle_key_down,
        set_focused,
        highlight_source,
        search_text,
        set_search_text,
    };
};

const use_virtualization = (options: {
    items_count: number;
    item_height: number;
    container_ref: React.RefObject<HTMLDivElement>;
    overscan?: number;
})=>{
    const {items_count, item_height, container_ref, overscan = 5} = options;
    const [scroll_top, set_scroll_top] = useState(0);
    const [container_height, set_container_height] = useState(0);
    useEffect(()=>{
        const container = container_ref.current;
        if (!container)
            return;
        const observer = new ResizeObserver(entries=>{
            for (const entry of entries)
                set_container_height(entry.contentRect.height);
        });
        observer.observe(container);
        set_container_height(container.clientHeight);
        return ()=>observer.disconnect();
    }, [container_ref]);
    const handle_scroll = useCallback((e: UIEvent<HTMLDivElement>)=>{
        set_scroll_top(e.currentTarget.scrollTop);
    }, []);
    const total_height = items_count * item_height;
    const start_idx = Math.max(0,
        Math.floor(scroll_top / item_height) - overscan);
    const visible_count = Math.ceil(container_height / item_height);
    const end_idx = Math.min(items_count - 1,
        start_idx + visible_count + overscan * 2);
    const offset_top = start_idx * item_height;
    return {
        total_height,
        start_idx,
        end_idx,
        offset_top,
        handle_scroll,
    };
};

export const Basic_select = (props: i_basic_select_props)=>{
    const {
        options: unfiltered_options = [],
        on_focus,
        on_blur,
        on_open,
        on_option_enter,
        on_option_leave,
        render_body,
        render_option,
        render_label,
        render_empty,
        render_loading,
        cls,
        disabled = false,
        tab_index = 0,
        with_full_search = false,
        full_search_placeholder = '',
        search_mode = 'starts_with',
        search_autofocus = false,
        position = 'right',
        virtualize = false,
        item_height = 36,
        overscan = 5,
        loading: external_loading = false,
        load_options,
    } = props;

    const [internal_loading, set_internal_loading] = useState(false);
    const options_loaded = useRef(false);
    const loading = external_loading || internal_loading;

    const {
        is_open,
        focused,
        highlighted_idx,
        value,
        has_selected_value,
        toggle_dropdown,
        handle_option_select,
        handle_key_down,
        set_focused,
        highlight_source,
        search_text,
        set_search_text,
    } = use_select(props);

    const container_ref = useRef<HTMLDivElement>(null);
    const list_ref = useRef<HTMLDivElement>(null);
    const dropdown_ref = useRef<HTMLDivElement>(null);
    const option_refs = useRef<{[key: string]: HTMLDivElement | null}>({});
    const search_input_ref = useRef<HTMLInputElement>(null);

    useEffect(()=>{
        if (!is_open || !load_options || options_loaded.current)
            return;
        set_internal_loading(true);
        load_options().then(()=>{
            options_loaded.current = true;
            set_internal_loading(false);
        }).catch(()=>{
            set_internal_loading(false);
        });
        if (on_open)
            on_open();
    }, [is_open, load_options, on_open]);

    const virt = use_virtualization({
        items_count: props.options?.length || 0,
        item_height,
        container_ref: list_ref,
        overscan,
    });

    const options = useMemo(()=>{
        if (with_full_search && search_text)
        {
            const search_lower = search_text.toLowerCase();
            return unfiltered_options.filter(opt=>{
                const label_lower = opt.label?.toLowerCase();
                if (!label_lower)
                    return false;
                const str_method = search_mode === 'starts_with' ?
                    'startsWith' : 'includes';
                return label_lower[str_method](search_lower);
            });
        }
        return unfiltered_options;
    }, [unfiltered_options, search_text, with_full_search, search_mode]);
    useEffect(()=>{
        const current_keys = options.map(opt=>String(opt.value));
        Object.keys(option_refs.current).forEach(key=>{
            if (!current_keys.includes(key))
                delete option_refs.current[key];
        });
    }, [options]);

    useEffect(()=>{
        if (!is_open || highlighted_idx < 0 || !options[highlighted_idx])
            return;
        if (highlight_source.current === 'keyboard' ||
            highlight_source.current === 'initial')
        {
            const list_element = list_ref.current;
            if (virtualize && list_element)
            {
                const target_scroll = highlighted_idx * item_height;
                const list_height = list_element.clientHeight;
                const current_scroll = list_element.scrollTop;
                if (target_scroll < current_scroll)
                    list_element.scrollTop = target_scroll;
                else if (target_scroll + item_height >
                    current_scroll + list_height)
                {
                    list_element.scrollTop = target_scroll - list_height
                        + item_height;
                }
            }
            else if (list_element)
            {
                const option_key = String(options[highlighted_idx].value);
                const option_element = option_refs.current[option_key];
                if (option_element)
                {
                    const list_rect = list_element.getBoundingClientRect();
                    const option_rect = option_element.getBoundingClientRect();
                    const is_above_visible = option_rect.top < list_rect.top;
                    const is_below_visible = option_rect.bottom >
                        list_rect.bottom;
                    if (is_above_visible || is_below_visible)
                        option_element.scrollIntoView({block: 'nearest'});
                }
            }
            highlight_source.current = null;
        }
    }, [is_open, highlighted_idx, options, virtualize, item_height]);

    useEffect(()=>{
        if (!is_open)
            return;
        const handle_outside_click = (e: MouseEvent)=>{
            const is_search_click = e.target &&
                (e.target as HTMLElement).closest('[data-elem="search"]');
            if (is_search_click)
                return;
            if (container_ref.current &&
                !container_ref.current.contains(e.target as Node))
            {
                toggle_dropdown();
                if (with_full_search)
                    set_search_text('');
            }
        };
        document.addEventListener('mousedown', handle_outside_click);
        return ()=>{
            document.removeEventListener('mousedown', handle_outside_click);
        };
    }, [is_open, toggle_dropdown]);

    useEffect(()=>{
        if (is_open && with_full_search && search_autofocus &&
            search_input_ref.current)
        {
            search_input_ref.current.focus();
        }
    }, [is_open, with_full_search, search_autofocus]);

    useLayoutEffect(()=>{
        if (!is_open || !container_ref.current || !dropdown_ref.current)
            return;
        const container = container_ref.current;
        const dropdown = dropdown_ref.current;
        const {bottom} = container.getBoundingClientRect();
        const viewport_height = window.innerHeight;
        const dropdown_height = dropdown.getBoundingClientRect().height;
        const space_below = viewport_height - bottom;
        if (space_below < dropdown_height)
        {
            const shift = dropdown_height - space_below;
            dropdown.style.transform = `translateY(-${shift+8}px)`;
        }
        else
            dropdown.style.transform = '';
    }, [is_open]);

    const handle_focus = useCallback((e: React.FocusEvent)=>{
        set_focused(true);
        if (on_focus)
            on_focus(e);
    }, [on_focus, set_focused]);

    const handle_blur = useCallback((e: React.FocusEvent)=>{
        set_focused(false);
        queueMicrotask(()=>{
            const related_target = e.relatedTarget as HTMLElement;
            if (!container_ref.current?.contains(document.activeElement) &&
                !dropdown_ref.current?.contains(document.activeElement) &&
                (!related_target ||
                !container_ref.current?.contains(related_target) &&
                !dropdown_ref.current?.contains(related_target)))
            {
                toggle_dropdown(false);
                if (with_full_search)
                    set_search_text('');
                if (on_blur)
                    on_blur(e);
            }
        });
    }, [on_blur, toggle_dropdown]);

    const handle_click = useCallback(()=>{
        toggle_dropdown();
    }, [toggle_dropdown]);

    const tab_index_value = disabled ? undefined : tab_index;
    const body_content = render_body({
        selected: has_selected_value,
        opened: is_open,
        value,
        focused,
        disabled,
    });
    const render_option_item = useCallback(
    (option: i_basic_select_option, idx: number)=>{
        const is_selected = option.value == value;
        const is_highlighted = idx == highlighted_idx;
        const option_content = render_option({
            option,
            selected: is_selected,
            highlighted: is_highlighted,
            focused: focused && is_highlighted,
        });
        return <div
          key={String(option.value)}
          ref={el=>option_refs.current[String(option.value)] = el}
          onClick={()=>handle_option_select(option)}
          onMouseEnter={on_option_enter
            ? e=>on_option_enter(option.value,
              e.currentTarget)
            : undefined}
          onMouseLeave={on_option_leave || undefined}
          data-elem="option"
          data-state-selected={`${is_selected}`}
          data-state-highlighted={`${is_highlighted}`}
          data-state-disabled={`${!!option.disabled}`}
          data-state-focused={`${focused && is_highlighted}`}>
          {option_content}
        </div>;
    }, [value, highlighted_idx, focused, render_option,
        handle_option_select, on_option_enter, on_option_leave]);

    const default_loader = ()=><div className={styles.loader}>
      <div className={styles.spinner}/>
    </div>;

    const options_list = useMemo(()=>{
        if (loading)
        {
            return <div data-elem="list" data-state="loading">
              {render_loading ? render_loading() : default_loader()}
            </div>;
        }
        if (!options.length)
        {
            return render_empty ? <div data-elem="list" data-state="empty">
              {render_empty()}
            </div> : null;
        }
        if (virtualize)
        {
            const visible_options = options.slice(virt.start_idx,
                virt.end_idx + 1);
            return <div ref={list_ref} data-elem="list"
              onScroll={virt.handle_scroll}
              className={styles.virtualized_list}>
              <div style={{height: virt.total_height, position: 'relative'}}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  transform: `translateY(${virt.offset_top}px)`,
                }}>
                  {visible_options.map((option, i)=>
                      render_option_item(option, virt.start_idx + i))}
                </div>
              </div>
            </div>;
        }
        return <div ref={list_ref} data-elem="list">
          {options.map((option, idx)=>render_option_item(option, idx))}
        </div>;
    }, [loading, options, render_empty, render_loading, virtualize,
        virt.start_idx, virt.end_idx, virt.total_height, virt.offset_top,
        virt.handle_scroll, render_option_item]);

    const search_input = with_full_search && is_open ?
      <div className={styles.search_container}
        onClick={e=>{
          e.preventDefault();
          e.stopPropagation();
        }}
        onMouseDown={e=>{
          e.stopPropagation();
        }}
        data-elem="search">
          <Basic_input
            cls={styles.search_input}
            value={search_text}
            on_change={val=>set_search_text(val || '')}
            placeholder={full_search_placeholder}
            slot_right={<Icon_search/>}
            input_ref={search_input_ref}
          />
      </div> : null;
    const root_cls = cn(styles.select_container, cls);
    return <div
      ref={container_ref}
      className={root_cls}
      tabIndex={tab_index_value}
      onFocus={handle_focus}
      onBlur={handle_blur}
      onKeyDown={handle_key_down}
      data-elem="sh_select"
      data-state-focused={`${focused}`}
      data-state-disabled={`${disabled}`}
      data-state-opened={`${is_open}`}
      data-state-empty={`${!has_selected_value}`}
      role="combobox"
      aria-expanded={`${is_open}`}
    >
      {render_label && <div data-elem="label">
        {render_label()}
      </div>}
      <div onClick={handle_click} data-elem="body">{body_content}</div>
      <div ref={dropdown_ref} data-elem="dropdown" role="listbox"
        className={cn({
          [styles.dropdown_left]: position == 'left',
        })}>
        {search_input}
        {options_list}
      </div>
    </div>;
};
